import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, ActionCreator } from 'redux';
import withStyles from 'react-jss';
import { withRouter } from 'react-router';
import NotificationsListView from './NotificationsListView';
import { Modal, Icon, Spin, Tooltip, Tabs } from 'antd';
import { commonStyles } from 'src/Library/CommonStyles';
import { StylesProps, RouterProps } from 'src/Library/Types';
import { markNotifications, getNotificationsPolling } from 'src/api/shipmentExecution';
import { NotifPagination, NotificationData, SourceObjectType } from './Notifications.interfaces';
import { fetchNotifications, resetNotifications, handleTabAndFilterChange } from 'src/actions/notificationsActions';
import { getNotificationsRoute } from 'src/routing/utils';
import { OrgTypes } from 'src/Library/Constants';
import { notificationCount } from './Notifications.types';

const { useEffect } = React;
const styles = (theme) => ({
  ...commonStyles(theme),
  notificationCount: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '5px',
    background: '#006EC3',
    marginLeft: '5px',
    width: '25px',
    height: '14px',
    color: '#FFFFFF',
    fontSize: '12px',
  },
  headerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 24px 16px 24px'
  },
  showMoreStyle: {
    cursor: 'pointer',
    color: '#006EC3',
    display: 'flex',
    justifyContent: 'center',
  },
  notifAndCount: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  notifStyle: {
    fontSize: '16px',
    lineHeight: '14px',
    fontWeight: 600,
    color: '#333333',
  },
  tabs: {
    padding: '0px 24px 0px 24px',
    '& .ant-tabs-nav .ant-tabs-tab-active': {
      color: '#000000',
      fontWeight: 'bold'
    },
    '& .ant-tabs-nav .ant-tabs-tab': {
      '&:hover': {
        color: '#000000',
      },
    },
    '& .ant-tabs-ink-bar': {
      height: '4px',
      backgroundColor: '#000000',
      borderRadius: '4px 4px 0px 0px'
    },
    '& .ant-tabs .ant-tabs-large-bar .ant-tabs-tab': {
      padding: '12px 16px'
    },
    '& .ant-tabs-nav-scroll': {
      paddingLeft: '10px'
    }
  }
});

interface ReduxProps {
  resetNotifications: () => ActionCreator<void>;
  fetchNotifications: ({source, queryString }?: { source?: string, queryString?: string }) => ActionCreator<void>;
  handleTabAndFilterChange: (params: { source?: string, queryString?: string }) => ActionCreator<void>;
  isLoading: boolean;
  notificationsData: NotificationData[];
  unreadCount: notificationCount;
  pagination: NotifPagination;
  sourceObjectType: string;
}
interface IProps extends RouterProps, StylesProps<ReturnType<typeof styles>>, ReduxProps {
  visible: boolean;
  type: string;
  handleOnCancel: () => void;
  currentModule: string;
  showExecution: boolean;
  showProcurement: boolean;
  showAirCargo: boolean;
  showRFQ: boolean;
}
const NotificationsModal = (props: IProps) => {
  const {
    classes,
    visible,
    isLoading,
    unreadCount,
    history,
    match,
    type,
    notificationsData,
    pagination,
    handleOnCancel,
    fetchNotifications,
    resetNotifications,
    sourceObjectType,
    handleTabAndFilterChange,
    currentModule,
    showExecution,
    showProcurement,
    showAirCargo,
    showRFQ,
  } = props;
  useEffect(() => {
    visible && handleTabAndFilterChange({ source: currentModule });
  }, [visible]);

  const handleMarkAll = async (params?: Object) => {
    const response = await markNotifications(params || {});
    if (response?.isSuccess) {
      resetNotifications();
      fetchNotifications();
    }
  };

  const renderHeader = () => {
    const totalUnreadCount = unreadCount?.all || 0;
    return <div className={classes.headerStyle}>
      <div className={classes.notifAndCount}>
        <span className={classes.notifStyle}>Notifications ({totalUnreadCount})</span>
      </div>
      <div className={classes.notifStyle} style={{ fontSize: '14px' }}>
        <span
          onClick={() => {
            totalUnreadCount > 0 && handleMarkAll()
          }}
          style={{
            color: totalUnreadCount > 0 ? '#006EC3' : '#888888',
            cursor: totalUnreadCount > 0 ? 'pointer' : 'not-allowed'
          }}>
          Mark all as read
            </span>
        <Tooltip title="Go To Notification Settings" placement="left">
          <Icon type="setting" theme="outlined" style={{ marginLeft: '10px', cursor: 'pointer' }} onClick={() => history.push('/account', { defaultActiveKey: 'notificationSettings' })} />
        </Tooltip>
      </div>
    </div>;
  };

  const renderSpin = () => {
    return (
      isLoading && (
        <div
          style={{
            width: "100%",
            textAlign: "center",
            zIndex: 1000,
          }}
        >
          <Spin size="default" />
        </div>
      )
    );
  };

  const handleViewAllClick = () => {
    const orgType = type === OrgTypes.SHIPPER ? 'shipper' : 'ff';
    history.push(getNotificationsRoute(orgType));
  };

  const renderViewAll = () => {
    return <div className={classes.showMoreStyle} onClick={() => handleViewAllClick()}>
      View All
    </div>;
  };

  const renderTabData = () => {
    if (isLoading) return renderSpin();
    return (
      notificationsData.map((val) => <NotificationsListView
        data={val}
        markNotification={handleMarkAll}
        page="MODAL" />)
    );
  };

  return (
    <Modal
      visible={visible}
      style={{ marginRight: '0.5%', marginTop: '-3.6%' }}
      bodyStyle={{ maxHeight: '5%', padding: '18px 0' }}
      footer={null}
      closable={false}
      onCancel={() => handleOnCancel()}
      width={500}
    >
      {renderHeader()}
      <Tabs
        className={classes.tabs}
        activeKey={sourceObjectType}
        onChange={(tabId) => handleTabAndFilterChange({ source: tabId })}
        size="large"
      >
          {showRFQ &&
            <Tabs.TabPane
              key={SourceObjectType.RFQ_PLAN}
              tab={<span>
                RFQ (<span>{isLoading ? <span className={'loadingDotsBlue'}>   </span> : (unreadCount?.rfq) || 0})</span>
              </span>}
            >
              {renderTabData()}
            </Tabs.TabPane>
          }
          {showProcurement &&
            <Tabs.TabPane
              key={SourceObjectType.INQUIRY}
              tab={<span>
                Procure (<span>{isLoading ? <span className={'loadingDotsBlue'}>   </span> : (unreadCount?.inquiry) || 0})</span>
              </span>}
            >
              {renderTabData()}
            </Tabs.TabPane>
          }
          {showExecution &&
            <Tabs.TabPane
              key={SourceObjectType.SHIPMENT}
              tab={<span>
                Execution (<span>{isLoading ? <span className={'loadingDotsBlue'}>   </span> : (unreadCount?.shipment) || 0})</span>
              </span>}
            >
              {renderTabData()}
            </Tabs.TabPane>
          }
          {showAirCargo &&
            <Tabs.TabPane
              key={SourceObjectType.HAWB}
              tab={<span>
                Air Cargo (<span>{isLoading ? <span className={'loadingDotsBlue'}>   </span> : (unreadCount?.hawb) || 0})</span>
              </span>}
            >
              {renderTabData()}
            </Tabs.TabPane>
          }
      </Tabs>
      {!isLoading && renderViewAll()}
    </Modal>
  );
};
const mapStateToProps = ({ notificationsReducer, masterData }, ownProps) => ({
  isLoading: notificationsReducer.isLoading,
  notificationsData: notificationsReducer.data,
  unreadCount: notificationsReducer.unreadCount,
  pagination: notificationsReducer.pagination,
  sourceObjectType: notificationsReducer.sourceObjectType,
  showProcurement: masterData.modules?.show_inquiry || masterData.modules?.show_inquiry_bidding,
  showExecution: masterData.modules?.show_shipment_execution_shipper || masterData.modules?.show_shipment_execution_vendor,
  showAirCargo: masterData.modules?.show_air_tracking,
  showRFQ: masterData.modules?.show_rfq
});
const mapDispatchToProps = dispatch => bindActionCreators({
  fetchNotifications,
  resetNotifications,
  handleTabAndFilterChange,
}, dispatch);
export default React.memo(withRouter(connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(NotificationsModal))));